import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { ClipboardList, HardHat, LayoutDashboard } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OfflineProvider } from "@/context/OfflineContext";
import { PendingUsersProvider } from "@/context/PendingUsersContext";
import { MetaProvider } from "@/context/MetaContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ProjectsPage from "@/pages/ProjectsPage";
import AdminPage from "@/pages/AdminPage";
import ProjectLayout from "@/pages/project/ProjectLayout";
import FactoryLayout from "@/pages/factory/FactoryLayout";
import FactoryPlaceholder from "@/pages/factory/FactoryPlaceholder";
import FactoryModelPage from "@/pages/factory/FactoryModelPage";
import FactoryOverviewPage from "@/pages/factory/FactoryOverviewPage";
import OverviewPage from "@/pages/project/OverviewPage";
import ModelPage from "@/pages/project/ModelPage";
import PanelListPage from "@/pages/project/PanelListPage";
import BomPage from "@/pages/project/BomPage";
import PlanningPage from "@/pages/project/PlanningPage";
import PanelPropertiesPage from "@/pages/project/PanelPropertiesPage";
import LogisticsPage from "@/pages/project/LogisticsPage";
import IssuesPage from "@/pages/project/IssuesPage";
import ActionPage from "@/pages/project/ActionPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import QrRedirectPage from "@/pages/QrRedirectPage";
import SettingsPage from "@/pages/SettingsPage";
import "@/App.css";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F8]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePasswordPage />;
  if (adminOnly && !(user.roles || []).includes("admin"))
    return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/"
        element={
          <Protected>
            <ProjectsPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected adminOnly>
            <AdminPage />
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <SettingsPage />
          </Protected>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <Protected>
            <ProjectLayout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="model" element={null} />
        <Route path="planning" element={<PlanningPage />} />
        <Route path="panels" element={<PanelListPage />} />
        <Route path="bom" element={<BomPage />} />
        <Route path="properties" element={<PanelPropertiesPage />} />
        <Route path="logistics" element={<LogisticsPage />} />
        <Route path="issues" element={<IssuesPage />} />
        <Route path="action" element={<ActionPage />} />
      </Route>
      <Route
        path="/projects/:projectId/factory"
        element={
          <Protected>
            <FactoryLayout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route
          path="overview"
          element={<FactoryOverviewPage />}
        />
        <Route
          path="model"
          element={<FactoryModelPage />}
        />
        <Route
          path="orders"
          element={
            <FactoryPlaceholder
              slug="orders"
              title="Orders & Production"
              description="Order tracking and the production flow will live here."
              icon={ClipboardList}
            />
          }
        />
        <Route
          path="assembly"
          element={
            <FactoryPlaceholder
              slug="assembly"
              title="Factory Assembly"
              description="Assembly operations will live here."
              icon={HardHat}
            />
          }
        />
      </Route>
      <Route
        path="/q/:projectRef/:blockName"
        element={
          <Protected>
            <QrRedirectPage />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <OfflineProvider>
        <MetaProvider>
          <PendingUsersProvider>
            <Toaster position="top-right" theme="dark" richColors />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </PendingUsersProvider>
        </MetaProvider>
      </OfflineProvider>
    </AuthProvider>
  );
}
